import { getAccountClassForType } from "@common/constants/domains/accounting/accountTypeHelpers";
import {
    compareAccountsByLeafGroup,
    leafGroupHeader,
    withGroupHeaders,
} from "@common/constants/domains/accounting/accountGroups";
import { displayAccountLabel } from "@common/helpers/accounting";
import { Account } from "@common/types/domains/accounting/account";
import { AccountClass } from "@common/constants/domains/accounting/accounts";
import React, { useCallback, useMemo, useState } from "react";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { Tag } from "@design-system/Tag/Tag";
import { DropdownCell } from "./DropdownCell";
import { BaseCellProps } from "./index";
import styles from "./AccountSearchCell.module.scss";

interface NewAccountInfo {
    name: string;
    code?: string | null;
}

interface AccountSearchCellProps extends BaseCellProps<string> {
    accounts: Account[];
    cta?: (
        search: string,
        closePopover: () => void,
        rowIdx: number,
    ) => React.ReactNode;
    newAccountInfo?: (row: Record<string, any>) => NewAccountInfo | undefined;
    /** Shown muted in an empty cell: the account applied when none is chosen. */
    placeholderAccount?: Account;
}

export const AccountSearchCell: React.FC<AccountSearchCellProps> = ({
    row,
    accounts,
    cta,
    newAccountInfo,
    placeholderAccount,
    rowIdx,
    ...props
}) => {
    const [search, setSearch] = useState<string>("");
    const showAccountNumbers = useShowAccountNumbers();

    // Get the accountClass from the same row
    const rowAccountClass = row.accountClass as AccountClass | null;

    const accountsById = useMemo(
        () =>
            accounts.reduce(
                (acc, account) => {
                    if (account.id) {
                        acc[account.id] = account;
                    }
                    return acc;
                },
                {} as Record<string, Account>,
            ),
        [accounts],
    );

    const options = useMemo(() => {
        const matchingAccounts = accounts.filter((account) => {
            // Filter out disabled accounts from dropdown options
            if (account.isDisabled) {
                return false;
            }

            // First filter by search text
            const matchesSearch =
                account.name
                    .toLowerCase()
                    .includes(search?.toLowerCase() ?? "") ||
                (account.codeText ?? "")
                    .toLowerCase()
                    .includes(search?.toLowerCase() ?? "");

            if (!matchesSearch) {
                return false;
            }

            // Filter by accountClass if present in the row
            if (rowAccountClass && account.type) {
                const accountClass = getAccountClassForType(account.type);
                if (accountClass !== rowAccountClass) {
                    return false;
                }
            }

            return true;
        });
        const orderedAccounts = matchingAccounts.toSorted(
            compareAccountsByLeafGroup,
        );
        return withGroupHeaders(
            orderedAccounts,
            leafGroupHeader.keyOf,
            leafGroupHeader.labelOf,
        ).map((c) => ({
            value: c.id ?? "",
            label: displayAccountLabel(c, showAccountNumbers),
            groupHeader: c.groupHeader,
        }));
    }, [accounts, search, rowAccountClass, showAccountNumbers]);

    const wrappedCta = useMemo(() => {
        if (!cta) {
            return undefined;
        }
        return (searchValue: string, closePopover: () => void) =>
            cta(searchValue, closePopover, rowIdx);
    }, [cta, rowIdx]);

    const resolvedNewAccountInfo = useMemo(
        () => newAccountInfo?.(row),
        [newAccountInfo, row],
    );

    const renderDisplay = useCallback(
        (value: string | null) => {
            if (!value && resolvedNewAccountInfo) {
                const label = resolvedNewAccountInfo.code
                    ? `${resolvedNewAccountInfo.code} - ${resolvedNewAccountInfo.name}`
                    : resolvedNewAccountInfo.name;
                return (
                    <div className={styles.accountDisplay}>
                        <BreakableText
                            text={label}
                            showTooltip
                            className={styles.accountLabel}
                        />
                        <Tag size="sm" color="neutral" outline>
                            New
                        </Tag>
                    </div>
                );
            }

            if (!value && placeholderAccount) {
                return (
                    <div className={styles.accountDisplay}>
                        <BreakableText
                            text={displayAccountLabel(
                                placeholderAccount,
                                showAccountNumbers,
                            )}
                            showTooltip
                            className={styles.accountPlaceholder}
                        />
                    </div>
                );
            }

            if (!value) {
                return null;
            }

            const account = accountsById[value];
            if (!account) {
                return null;
            }

            const label = displayAccountLabel(account, showAccountNumbers);

            return (
                <div className={styles.accountDisplay}>
                    <BreakableText
                        text={label}
                        showTooltip
                        className={styles.accountLabel}
                    />
                    {account.isDisabled && (
                        <Tag size="sm" color="neutral">
                            Archived
                        </Tag>
                    )}
                </div>
            );
        },
        [
            accountsById,
            showAccountNumbers,
            resolvedNewAccountInfo,
            placeholderAccount,
        ],
    );

    return (
        <DropdownCell
            {...props}
            row={row}
            rowIdx={rowIdx}
            options={options}
            setSearch={setSearch}
            renderDisplay={renderDisplay}
            cta={wrappedCta}
        />
    );
};
